import LZString from 'lz-string';
import mixpanel from 'mixpanel-browser';
import { useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { PageNavTrackingEvents, PagePaths } from '@revelio/core';

import type { PageState } from '../types';

/**
 * Composition-over-time charts have nested subfilters. When a deeplink arrives
 * with non-default subfilters we want those values to become the new default,
 * otherwise the data provider fires a request for every non default subfilter because it thinks the user
 * just changed the filters after hydration. To avoid the duplicate request we
 * treat the URL selections as the baseline by copying them into initialSelections.
 */
const normalizeCompositionSubfiltersFromUrl = (
  pageState: PageState | undefined
): PageState | undefined => {
  if (!pageState) return pageState;

  const hasSelections = Object.keys(pageState.selections ?? {}).length > 0;
  if (!hasSelections) return pageState;

  // Normalize: set initialSelections to match selections from URL
  // This treats URL selections as the baseline (not modified)
  return {
    ...pageState,
    initialSelections: { ...pageState.selections },
  };
};

export const useDeserializePageStateFromUrl = (enabled = true) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const pageStateReadFromUrl = useRef(false);

  return (() => {
    if (!enabled) {
      return;
    }

    if (location.pathname.startsWith(`/${PagePaths.REPORT_BUILDER}`)) {
      return;
    }

    if (pageStateReadFromUrl.current) return;

    const pageStateFromUrl = searchParams.get('pageState');

    pageStateReadFromUrl.current = true;

    if (!pageStateFromUrl) return;

    try {
      const urlStateSerialized =
        LZString.decompressFromEncodedURIComponent(pageStateFromUrl);
      const parsedState = JSON.parse(urlStateSerialized) as PageState;
      // Only after a successful parse — a corrupt payload shouldn't record a
      // deeplink navigation that never actually applied any state.
      mixpanel.track(PageNavTrackingEvents.NAVIGATION_INITIATED, {
        navigation_source: 'deeplink',
        deeplink_param: 'pageState',
        path: location.pathname,
      });
      const defaults: Partial<PageState> = {
        activeTabId: '',
        metricOverrides: {},
        selections: {},
        primaryEntities: {},
        multiPrimaryEntities: {},
        dimensionSourceOverrides: {},
        chartSwitcherPlotId: null,
        initialSelections: {},
      };
      const mergedState = { ...defaults, ...parsedState };
      return normalizeCompositionSubfiltersFromUrl(mergedState);
    } catch (error) {
      console.error('Failed to parse page state from URL:', error);
      return;
    }
  })();
};
